import { useEffect, useState } from "react";
import { Icon, Modal } from "@legal-500/legal500-design-system";
import LoadingState from "@/components/LoadingState";
import { templates, type MailingTemplate } from "@/api/templates.service";
import styles from "./MailingTemplates.module.css";

type PreviewModalProps = {
  template: MailingTemplate;
  onClose: () => void;
};

/**
 * Fetches a template's active SendGrid version and renders its HTML in a
 * sandboxed iframe (via `srcdoc`) so untrusted markup can never touch the host
 * page DOM, run scripts, or navigate the app. Handlebars placeholders such as
 * `{{first_name}}` appear literally — there is no merge-field substitution in
 * v1 (Q2).
 */
export function PreviewModal({ template, onClose }: PreviewModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<{ subject: string; html: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    templates
      .preview(template.sendgrid_template_id)
      .then(resp => {
        if (cancelled) return;
        setContent({ subject: resp.subject, html: resp.html_content });
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load preview");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [template.sendgrid_template_id]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={template.name ?? "Template preview"}
      size="medium"
      blurBackground
    >
      <div className={styles.previewBody}>
        {loading && <LoadingState message="Loading preview…" />}

        {error && !loading && (
          <div className={styles.previewError}>
            <Icon icon="exclamation" size="small" />
            <span>{error}</span>
          </div>
        )}

        {content && !loading && !error && (
          <>
            <div className={styles.previewSubject}>
              <span className={styles.previewSubjectLabel}>Subject:</span>
              <span>{content.subject || "—"}</span>
            </div>
            <iframe
              className={styles.previewFrame}
              title={`Preview of ${template.name ?? template.sendgrid_template_id}`}
              sandbox=""
              srcDoc={content.html}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
